import MultiOscillatorSynth from './MultiOscillatorSynth.js';
import {shuffle} from 'utils/ShuffleArray.js';
import Slideable from "instruments/Slideable.js";
import PatchSimplifier from "instruments/PatchSimplifier.js";
import {voiceFactory} from './oscillator/VoiceFactory.js';
import PolyParameter from 'parameters/PolyParameter.js';
import KeyScaled from '../KeyScaled.js';
import {bulkUpdate} from 'utils/bulkUpdate.js';
import PitchBendable from '../PitchBendable.js';
import {getImaginaryReal, getImaginaryRealSync} from 'constants/waveforms.js';
import {loadParameterFromJson, saveParameterToJson} from 'parameters/parameterJsonFunctions.js';
import {sleep} from 'utils/sleep.js';
import {uuid} from 'uuidv4';
import AlgoMixin from './AlgoMixin.js';
import AmpEnvelope from './AmpEnvelope.js';
import MasterAmpEnvelope from './MasterAmpEnvelope.js';
import {AbstractParameter, IntegerParameter} from 'parameters/AbstractParameter.js';
import {AudioParamValueParameter,
        AbstractAudioParamParameter,
        } from 'parameters/AbstractAudioParamParameter.js';
import {
    setTrackIsLoading, 
    setEffectParameterValues
} from 'actions/index.js';
import Tone from 'tone';
import {OSCILLATOR} from 'constants/effect-types.js';
import MacroMixin from 'effects/MacroMixin.js';
import ConfigurableRack from 'effects/rack/ConfigurableRack.js';
import ModEdge from './oscillator/ModEdge.js';
import RackableEffect from "effects/rack/RackableEffect.js"
import Filtering from './Filtering.js';
import SynthTriggerer from './SynthTriggerer.js';
import SynthParameters from './SynthParameters.js';
import SynthOutputs from './SynthOutputs.js';
import SynthFM from './SynthFM.js';
import SynthOscillators from './SynthOscillators.js';
import SynthJson from './SynthJson.js';
import VoiceFrequencies from './VoiceFrequencies.js';
import AmpMixin from './outputs/AmpMixin.js';
import VoiceManaged from './VoiceManaged.js';
import {compose} from 'utils/compose.js';

class RackSynth extends compose(
    VoiceFrequencies,
    SynthJson,
    SynthOscillators,
    SynthFM,
    SynthOutputs,
    SynthTriggerer,
    SynthParameters,
    Filtering,
    KeyScaled,
    PitchBendable,
    RackableEffect,
    ConfigurableRack,
    AlgoMixin,
    Slideable,
    PatchSimplifier,
    MacroMixin)(MultiOscillatorSynth)
{
    constructor(sequencerEngine, mixSession, context, track) {
        super(sequencerEngine, mixSession, context, false);

        this.id = uuid();
        this.initializeConfigurations(this.adjacencyLists);
        this.loaded = true;

        getImaginaryReal('01_Saw');
        getImaginaryReal('03_Square');

        this.setupOscillators();
        this.setupOutputs();
        this.setupFilters();

        // output node is the filter yall but later on we may want
        // to do other shit
        this.outputNode = this.filter.effectNode;
        this.outputNode.x = 180;
        this.outputNode.y = 200;

        this.track = track;
        this.setupFM();
        this.setupPitchBend();
        
        this.getParameters().forEach(
            x => x.isLoaded = true);

        this.setupParameters();
        this.refreshParameters();

        this.voiceFactory = voiceFactory;

        this.sequencerEngine.dispatch(
            setEffectParameterValues(
                this.initialBulkValues));

        this.setupSlide();
   }

    get detuneSignal() {
        return this.pitchBendSignal;
    }

    get detuneParameter() {
        return this.pitchBendParameter;
    }
    
    get detuneSignals() {
        let signals = [];
        this.oscillators.forEach(
            o => o.voices.forEach(
                v => signals.push(v.detune)));
        return signals;
    }

    get effects() {
        return this.oscillatorNodes;
    }

    set effects(x) {
    }

    newEdge(node, outputNumber) {
        return new ModEdge(node, this.sequencerEngine, outputNumber);
    }

    connectEdge(node1, edge) {
        super.connectEdge(node1, edge);
        node1.connectEdge(edge);
        return edge;
    }

    deleteEdge(edge) {
        if (!edge.modulator) {
            for (let id in this.adjacencyLists) {
                let adjacencyList = this.adjacencyLists[id];
                let indexOf =  adjacencyList.indexOf(edge); 
                if (indexOf > -1) {
                    let effect = this.effects.find(e => e.id === id);
                    if (effect) {
                        if (edge.disconnect) {
                            edge.disconnect();
                        }
                    }
                }
            }
        }

        super.deleteEdge(edge);
    }

    getEffectType() {
        return OSCILLATOR;
    }

    get modulators() {
        return [
            ... this.sequencerEngine.lfos.map(l => l.lfo)];
   }

    setSize(width, height, nodeWidth, nodeHeight) {
        this.inputNode.x = 0;
        this.inputNode.y = 0;
        this.outputNodeX.x = width - 32;
        this.outputNodeX.y = 50;
        this.outputNodeY.x = width - 32;
        this.outputNodeY.y = 100;
    }
        
    dispose() {
        this.deleteAlgorithmRouting();
        this.disposeOutputs();
        super.dispose();
    }

    getCopy(context, track, sequencerEngine=this.sequencerEngine, ignoreId) {
        let synth = new RackSynth(sequencerEngine, this.mixSession, context, track);
        synth.fromJsonSync(this.getJson(), ignoreId);
        return synth;
    }

    // simplifies a patch by removing modulations and formants
    simplifyPatch(percentage, formantPercentage=percentage/2) {
        super.simplifyPatch(percentage);

        // now look for all formants and delete percentage of them
        let enabledFormants = this.oscillators
            .flatMap(o => o.formantsManager.formants)
            .filter(f => f.isEnabled);
        enabledFormants = shuffle(enabledFormants);

        let toDelete = Math.floor(enabledFormants.length * (formantPercentage)/100);
        for (let i=0; i < toDelete; i++) {
            let formant = enabledFormants[i];
            let frmtParams = formant.getParameters();
            let mapped = frmtParams.filter(
                x => x.isMapped);
            mapped.forEach(
                p => p.isMapped.unmap(p));
            if (formant.effect) {
               formant.bypassEffect();
            }
            formant.enabledParameter.set(false);
        }
    }
}

export default RackSynth;
